import { useMemo, useState, type ComponentType, type ReactElement, type SVGProps } from "react";
import { useNavigate } from "react-router";
import type { KeybindAction, PresenceStatusChoice } from "@atrium/shared";
import { getPrefs, savePrefs, setTheme, toggleTheme, usePrefs } from "../../lib/prefs";
import { presenceActions, STATUS_LABELS } from "../../lib/presence";
import { useUI } from "../../lib/uiStore";
import { PresenceIndicator } from "../../components/PresenceIndicator";
import { confirmSignOut } from "../../components/confirmations";
import {
  IconAccessibility,
  IconBell,
  IconBookmark,
  IconCheckRead,
  IconCommunities,
  IconCompose,
  IconExplore,
  IconFeed,
  IconKeyboard,
  IconMessages,
  IconMonitor,
  IconMoon,
  IconPalette,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSidebar,
  IconSmile,
  IconSun,
  IconTranslation,
  IconLogout,
  IconUser,
  IconShieldCheck,
  IconCommand,
} from "../../components/icons";
import { useAuth, useMe } from "../auth/AuthProvider";
import { comboLabel, isSingleKey } from "../settings/keybinds";
import { fuzzyFilter } from "./fuzzy";
import { KeyHint, PaletteDialog, type PaletteItem } from "./PaletteDialog";
import { focusSearchInput, markAllRead } from "./actions";

/*
 * Ctrl/Cmd+Shift+P: every app action by name — navigation, creating things,
 * settings sections, theme, status, mark all as read, help.
 */

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

interface Command {
  id: string;
  group: string;
  label: string;
  icon: Icon | (() => ReactElement);
  keywords?: string;
  action?: KeybindAction;
  run: () => void;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const me = useMe();
  const { logout } = useAuth();
  const prefs = usePrefs();
  const { openComposer, setCreateCommunityOpen, toggleMembers, setOverlay, lastCommunityPath } = useUI();
  const [q, setQ] = useState("");

  const commands = useMemo<Command[]>(() => {
    const status = (s: PresenceStatusChoice): Command => ({
      id: `status:${s}`,
      group: "Status",
      label: `Set status: ${STATUS_LABELS[s].toLowerCase()}`,
      icon: () => <PresenceIndicator status={s === "invisible" ? "offline" : s} size="md" label={false} className="[--presence-ring:transparent]" />,
      keywords: "presence away busy",
      run: () => void presenceActions.setStatus(s),
    });
    const section = (id: string, label: string, icon: Icon, keywords = ""): Command => ({ id: `settings:${id}`, group: "Settings", label: `Settings: ${label}`, icon, keywords, run: () => navigate(`/settings/${id}`) });
    return [
      { id: "go:communities", group: "Go to", label: "Communities", icon: IconCommunities, action: "goCommunities", run: () => navigate(lastCommunityPath || "/c") },
      { id: "go:feed", group: "Go to", label: "Feed", icon: IconFeed, action: "goFeed", keywords: "home timeline", run: () => navigate("/feed") },
      { id: "go:explore", group: "Go to", label: "Explore", icon: IconExplore, action: "goExplore", keywords: "discover", run: () => navigate("/explore") },
      { id: "go:messages", group: "Go to", label: "Messages", icon: IconMessages, action: "goMessages", keywords: "dm direct", run: () => navigate("/messages") },
      { id: "go:notifications", group: "Go to", label: "Notifications", icon: IconBell, action: "goNotifications", run: () => navigate("/notifications") },
      { id: "go:saved", group: "Go to", label: "Saved", icon: IconBookmark, keywords: "bookmarks", run: () => navigate("/saved") },
      { id: "go:profile", group: "Go to", label: "Your profile", icon: IconUser, run: () => navigate(`/u/${me.username}`) },
      { id: "search", group: "Go to", label: "Search", icon: IconSearch, action: "search", keywords: "find messages posts", run: () => (navigate("/search"), focusSearchInput()) },
      { id: "switcher", group: "Go to", label: "Quick switcher", icon: IconCommand, action: "quickSwitcher", keywords: "jump", run: () => setOverlay("switcher") },
      { id: "new:post", group: "Create", label: "New post", icon: IconCompose, action: "newPost", keywords: "write compose", run: () => openComposer() },
      { id: "new:community", group: "Create", label: "Create a community", icon: IconPlus, keywords: "server new", run: () => setCreateCommunityOpen(true) },
      { id: "read-all", group: "Actions", label: "Mark everything as read", icon: IconCheckRead, action: "markAllRead", keywords: "unread clear", run: () => void markAllRead() },
      { id: "members", group: "Actions", label: "Show or hide members", icon: IconSidebar, action: "toggleMembers", run: () => toggleMembers() },
      { id: "theme:toggle", group: "Appearance", label: "Toggle light and dark theme", icon: IconMoon, keywords: "theme mode", run: () => void toggleTheme() },
      { id: "theme:dark", group: "Appearance", label: "Theme: dark", icon: IconMoon, run: () => void setTheme("dark") },
      { id: "theme:light", group: "Appearance", label: "Theme: light", icon: IconSun, run: () => void setTheme("light") },
      { id: "theme:system", group: "Appearance", label: "Theme: match system", icon: IconMonitor, run: () => void setTheme("system") },
      {
        id: "contrast",
        group: "Appearance",
        label: getPrefs().highContrast ? "Turn off high contrast" : "Turn on high contrast",
        icon: IconAccessibility,
        run: () => void savePrefs({ highContrast: !getPrefs().highContrast }),
      },
      {
        id: "motion",
        group: "Appearance",
        label: prefs.reduceMotion === "on" ? "Allow animations" : "Reduce motion",
        icon: IconAccessibility,
        keywords: "animation",
        run: () => void savePrefs({ reduceMotion: getPrefs().reduceMotion === "on" ? "off" : "on" }),
      },
      status("online"),
      status("idle"),
      status("dnd"),
      status("invisible"),
      { id: "status:custom", group: "Status", label: "Set a custom status…", icon: IconSmile, run: () => setOverlay("status") },
      section("appearance", "appearance", IconPalette, "theme accent colour font typography navigation"),
      section("accessibility", "accessibility", IconAccessibility, "contrast motion autoplay"),
      section("keyboard", "keyboard shortcuts", IconKeyboard, "keybinds hotkeys"),
      section("notifications", "notifications", IconBell),
      section("privacy", "privacy & safety", IconShieldCheck, "presence online status"),
      section("region", "language & region", IconTranslation, "locale time date"),
      { id: "settings", group: "Settings", label: "Open settings", icon: IconSettings, action: "goSettings", run: () => navigate("/settings") },
      { id: "shortcuts", group: "Help", label: "Keyboard shortcuts", icon: IconKeyboard, action: "shortcutHelp", run: () => setOverlay("shortcuts") },
      { id: "signout", group: "Account", label: "Sign out", icon: IconLogout, run: async () => (await confirmSignOut()) && void logout() },
    ];
  }, [navigate, me.username, prefs.reduceMotion, lastCommunityPath, openComposer, setCreateCommunityOpen, toggleMembers, setOverlay, logout]);

  const hint = (a?: KeybindAction) => {
    const combo = a ? prefs.keybinds[a] : "";
    if (!combo || (!prefs.singleKeyShortcuts && isSingleKey(combo))) return undefined;
    return <kbd className="font-sans">{comboLabel(combo)}</kbd>;
  };

  const sections = useMemo(() => {
    const toItem = (c: Command): PaletteItem => {
      const I = c.icon;
      return { id: c.id, label: c.label, icon: <I className="icon size-[18px]" aria-hidden />, hint: hint(c.action), run: c.run };
    };
    if (q.trim()) return [{ items: fuzzyFilter(commands, q, (c) => [c.label, `${c.group} ${c.label}`, c.keywords]).slice(0, 30).map(toItem) }];
    const groups = [...new Set(commands.map((c) => c.group))];
    return groups.map((g) => ({ title: g, items: commands.filter((c) => c.group === g).map(toItem) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commands, q, prefs.keybinds, prefs.singleKeyShortcuts]);

  return (
    <PaletteDialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setQ("");
      }}
      label="Command palette"
      placeholder="Type a command"
      query={q}
      onQueryChange={setQ}
      sections={sections}
      empty={`No commands match “${q}”`}
      footer={
        <>
          <KeyHint keys={["↑", "↓"]} label="Move" />
          <KeyHint keys={["Enter"]} label="Run" />
        </>
      }
    />
  );
}
